import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Box, Typography, Popover, Button, TextField, Chip,
    RadioGroup, FormControlLabel, Radio, FormControl
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import DeleteIcon from '@material-ui/icons/Delete';

const LABELS = [
    'Not achieved',
    'Incorrect execution',
    'Partially correct execution with teacher support',
    'Correct execution independently'
];

const buildRanges = (maxScore) => {
    const max = Number(maxScore) || 0;
    const q = Math.floor(max / 4);
    return [
        [0, q - 1],
        [q, 2 * q - 1],
        [2 * q, 3 * q - 1],
        [3 * q, max]
    ];
};

const useStyles = makeStyles((theme) => ({
    uploadButton: {
        minWidth: 140,
        color: theme.palette.common.gamsDarkBlue,
        borderColor: theme.palette.common.gamsDarkBlue,
        [theme.breakpoints.down('xs')]: {
            minWidth: 'auto',
            flex: 1,
        },
        '&:hover': {
            backgroundColor: theme.palette.common.blue[600],
            color: theme.palette.common.white,
        },
        '&:focus': {
            outline: 'none',
        },
        '&:active': {
            backgroundColor: theme.palette.common.blue[600],
            color: theme.palette.common.white,
        },
    },
    buttonsContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
            gap: theme.spacing(1),
        },
    },
    radioLabel: {
        marginBottom: theme.spacing(0.5),
    },
}));

const ValidationPopover = ({
    open,
    anchorEl,
    onClose,
    onConfirm,
    stepIndex,
    evaluationCriteria,
    maxScore
}) => {
    const classes = useStyles();
    const [validationNotes, setValidationNotes] = useState('');
    const [uploadedFile, setUploadedFile] = useState(null);
    const [selectedEval, setSelectedEval] = useState('');

    const ranges = buildRanges(maxScore);

    const isMobileDevice = () => {
        const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const isSmallScreen = window.innerWidth <= 768;
        return isMobileUA || isSmallScreen;
    };

    const handleClose = () => {
        setValidationNotes('');
        setUploadedFile(null);
        setSelectedEval('');
        onClose();
    };

    const handleConfirm = () => {
        let finalNote = '';
        if (evaluationCriteria && selectedEval) {
            finalNote = validationNotes.trim()
                ? `${selectedEval} - ${validationNotes.trim()}`
                : selectedEval;
        } else {
            finalNote = validationNotes;
        }
        onConfirm(finalNote, uploadedFile);
        setValidationNotes('');
        setUploadedFile(null);
        setSelectedEval('');
    };

    const handleUploadPhoto = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        if (isMobileDevice()) {
            input.capture = 'environment';
        }
        input.onchange = (event) => {
            const file = event.target.files[0];
            if (file) setUploadedFile(file);
        };
        input.click();
    };

    const handleRemoveFile = () => {
        setUploadedFile(null);
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <Box style={{
                padding: '16px',
                minWidth: '280px',
                maxWidth: 'min(500px, 90vw)',
                width: '100%'
            }}>
                <Typography variant="h6" component="h3" gutterBottom>
                    Validazione Step {stepIndex + 1}
                </Typography>

                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '12px' }}>
                    È possibile, prima della validazione, aggiungere delle note e caricare una foto
                </Typography>

                {/* RADIO BUTTON EVALUATION CRITERIA */}
                {evaluationCriteria && (
                    <FormControl component="fieldset" fullWidth style={{ marginBottom: '12px' }}>
                        <Typography variant="body2" style={{ marginBottom: 8, fontWeight: 500 }}>
                            Evaluation criteria
                        </Typography>
                        <RadioGroup
                            value={selectedEval}
                            onChange={(e) => setSelectedEval(e.target.value)}
                        >
                            {LABELS.map((label, i) => {
                                const [start, end] = ranges[i];
                                const rangeLabel = end >= start
                                    ? `${start} - ${end} ${label}`
                                    : `${start} ${label}`;
                                return (
                                    <FormControlLabel
                                        key={label}
                                        value={rangeLabel}
                                        control={<Radio color="primary" />}
                                        label={rangeLabel}
                                        className={classes.radioLabel}
                                    />
                                );
                            })}
                        </RadioGroup>
                    </FormControl>
                )}

                {/* AREA DI TESTO */}
                <TextField
                    multiline
                    minRows={4}
                    fullWidth
                    placeholder="Note"
                    variant="outlined"
                    margin="normal"
                    value={validationNotes}
                    onChange={(e) => setValidationNotes(e.target.value)}
                />

                {/* FILE CARICATO */}
                {uploadedFile && (
                    <Box style={{ margin: '12px 0' }}>
                        <Chip
                            icon={<CameraAltIcon />}
                            label={uploadedFile.name}
                            onDelete={handleRemoveFile}
                            deleteIcon={<DeleteIcon />}
                            variant="outlined"
                            color="primary"
                        />
                    </Box>
                )}

                {/* BOTTONI */}
                <Box className={classes.buttonsContainer}>
                    <Button
                        variant="outlined"
                        onClick={handleUploadPhoto}
                        startIcon={isMobileDevice() ? <CameraAltIcon /> : <CloudUploadIcon />}
                        className={classes.uploadButton}
                        style={{ marginRight: '16px' }}
                    >
                        {isMobileDevice() ? 'Scatta foto' : 'Carica foto'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        style={{ backgroundColor: '#4caf50', color: 'white' }}
                    >
                        Valida Step
                    </Button>
                </Box>
            </Box>
        </Popover>
    );
};

export default ValidationPopover;
